import React, { useState, useEffect } from "react";
import allApi from "../../../../api/allApi";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import eyeIcon from "../../../../assets/images/eye-close.svg";
import eyeCloseIcon from "../../../../assets/images/eye-close.svg";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import errorIcon from "../../../../assets/images/error-2.png";
import { useLocalization } from "../../localization/LocalizationContext";
import { useNetwork } from "../../common/networkContext";
import {
  asList,
  flagString,
  isSsidName,
  isVlanId,
  isWpaPassword,
  needsWpaPassword,
} from "../../common/configValidation";

function AddSSIDModal({ getList, onClose }) {
  const navigate = useNavigate();
  const { localizationStrings } = useLocalization();
  
  // Get selected network from network context
  const { selectedNetworkId } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;

  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showData, setShowData] = useState(false);
  const [showPopUp, setShowPopUp] = useState(true);
  const [popUp, setPopUp] = useState(false);
  const [showSsidLimtPopup, setShowSsidLimitPopup] = useState(false);
  const [formData, setFormData] = useState({
    ssid: "",
    broadcastStatus: "1",
    band: [],
    networkName: networkId || "", // Pre-populate networkId
    vlanId: "",
    bandSteeringSt: "0",
    fastRoamingSt: "0",
    clientIsolation: "0",
    allowLocalUIAccess: "0",
    scheduleEnabled: "0",
    kvrStatus: "0",
    securityAuthentication: "",
    multiPsK: false,
    password: "",
    selectedSchedule: "",
    schedule: "",
    ieee80211w: "0",
  });
  const [schedule, setSchedule] = useState({
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  });
  const [schedules, setSchedules] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [vlanList, setVlanList] = useState([]);
  const [isCreatingNewSchedule, setIsCreatingNewSchedule] = useState(false);
  const [newScheduleName, setNewScheduleName] = useState("");
  const [scheduleName, setScheduleName] = useState("");
  const [schedulesList, setSchedulesList] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(true);
  const [slotsBooked, setSlotsBooked] = useState(false);
  const [selectedSSID, setSelectedSSID] = useState(null);
  const [networkIds, setNetworkIds] = useState([]);
  const [selectedExistingSchedule, setSelectedExistingSchedule] = useState("");

  // Sync networkName form data when active networkId changes
  useEffect(() => {
    if (networkId) {
      setFormData((prev) => ({
        ...prev,
        networkName: prev.networkName || networkId,
      }));
      fetchSchedules(networkId);
    }
  }, [networkId]);

  useEffect(() => {
    fetchVlanList(networkId);
    fetchNetworkIds();
    if (selectedSSID) {
      createScheduleSetting(selectedSSID);
    }
  }, [selectedSSID, networkId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      if (name === "band") {
        if (value === "All") {
          setFormData((prevData) => ({
            ...prevData,
            band: checked ? ["2.4 GHz", "5 GHz"] : [],
          }));
        } else if (type === "radio") {
          setFormData((prevData) => ({
            ...prevData,
            [name]: value,
          }));
        } else {
          setFormData((prevData) => {
            const updatedBand = checked
              ? [...prevData.band, value].filter(
                  (v, i, arr) => arr.indexOf(v) === i
                )
              : prevData.band.filter((b) => b !== value);

            return {
              ...prevData,
              band: updatedBand,
            };
          });
        }
      } else {
        setFormData((prevData) => ({
          ...prevData,
          [name]: checked ? "1" : "0",
        }));
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const fetchVlanList = async (currentNetworkId = networkId) => {
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/vlan/list?networkId=${currentNetworkId || networkId}`
      );
      setVlanList(asList(response));
    } catch (error) {
      console.error("Error fetching VLAN list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e, actionType) => {
    e.preventDefault();
    try {
      if (!isFormValid()) return;
      const payload = {
        ssid: formData.ssid.trim(),
        broadcastStatus: flagString(formData.broadcastStatus),
        band: formData.band.join(", "),
        networkId: formData.networkName || networkId,
        vlanId: formData.vlanId,
        bandSteeringSt: flagString(formData.bandSteeringSt),
        fastRoamingSt: flagString(formData.fastRoamingSt),
        clientIsolation: flagString(formData.clientIsolation),
        allowLocalUIAccess: flagString(formData.allowLocalUIAccess),
        scheduleEnabled: flagString(formData.scheduleEnabled),
        kvrStatus: flagString(formData.kvrStatus),
        securityAuthentication: formData.securityAuthentication,
        ieee80211w: formData.ieee80211w || "0",
        password: formData.password,
        schedule:
          formData.scheduleEnabled === "1"
            ? {
                scheduleName: formData.selectedSchedule || newScheduleName,
                allDay: formData.allDay,
                scheduleSetting: createScheduleSetting(schedule),
              }
            : undefined,
      };

      setLoading(true);
      setRefreshing(true);
      const api = new allApi();
      const response = await api.postRequest(
        "api/wireless/configuration/addSsid",
        "",
        payload
      );

      if (response.message) {
        setShowPopUp(false);
        setShowData(true);
        if (actionType === "saveAndConfigure") {
          navigate(`/wifi-settings`);
        }
      }
    } catch (error) {
      if (error.response?.data?.message === "ssid limit exceeded") {
        setShowSsidLimitPopup(true);
      } else {
        const errorMsg =
          error.response?.data?.message ||
          "An unexpected error occurred while adding the SSID.";
        console.error("Error adding SSID:", errorMsg);
      }
      console.error("Error adding SSID:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const createScheduleSetting = (schedule) => {
    const defaultStart = "2359";
    const defaultEnd = "0000";

    const daysOfWeek = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    const scheduleData = {
      schDays: "1111111",
    };

    daysOfWeek.forEach((day, index) => {
      scheduleData[`schOnTime${index}`] =
        schedule[day]?.[0]?.startTime || defaultStart;
      scheduleData[`schOffTime${index}`] =
        schedule[day]?.[0]?.endTime || defaultEnd;
    });

    return scheduleData;
  };

  const handleSuccessClose = async () => {
    setShowData(false);
    if (typeof getList === "function") {
      await getList();
    }
    if (typeof onClose === "function") {
      onClose();
    } else {
      navigate("/wifi-config");
    }
  };

  const handlePopUpClose = () => {
    setShowPopUp(false);
    if (typeof onClose === "function") {
      onClose();
    } else {
      navigate("/wifi-config");
    }
  };

  const MAX_TIME_SLOTS = 3;
  const addTimeSlot = (day) => {
    const updatedSchedule = { ...schedule };

    if (updatedSchedule[day].length < MAX_TIME_SLOTS) {
      updatedSchedule[day].push({ startTime: null, endTime: null });
      setSchedule(updatedSchedule);
    } else {
      console.log(`Maximum of ${MAX_TIME_SLOTS} time slots reached for ${day}`);
    }
  };

  const removeTimeSlot = (day, index) => {
    const updatedSchedule = { ...schedule };
    updatedSchedule[day].splice(index, 1);
    setSchedule(updatedSchedule);
  };

  const handleScheduleChange = (day, index, field, value) => {
    const updatedSchedule = { ...schedule };
    updatedSchedule[day][index][field] = value;
    setSchedule(updatedSchedule);
  };

  const handlePopUp = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      if (name === "band") {
        if (value === "All") {
          setFormData({
            ...formData,
            band: checked ? ["2.4 GHz", "5 GHz"] : [],
          });
        } else {
          setFormData((prevData) => {
            const newBand = checked
              ? [...prevData.band, value].filter((v) => v !== "All")
              : prevData.band.filter((band) => band !== value);
            return {
              ...prevData,
              band: newBand,
            };
          });
        }
      } else {
        setFormData({
          ...formData,
          [name]: checked ? "1" : "0",
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    setPopUp((prevState) => !prevState);
  };

  const fetchSchedules = async (targetNetworkId) => {
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/configuration/getAllSsidSchedule?networkId=${targetNetworkId}`
      );
      if (response.status === "OK") {
        setSchedules(response.response || []);
      }
    } catch (error) {
      console.error("Error fetching schedules:", error);
    }
  };

  const fetchNetworkIds = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/network/getAssociatedNetwork");
      if (
        response &&
        response.status === "OK" &&
        Array.isArray(response.response)
      ) {
        setNetworkIds(
          response.response.map((network) => ({
            network_id: network.network_id,
            network_name: network.network_name,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching network IDs:", error);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleScheduleToggle = () => {
    setIsCreatingNewSchedule(!isCreatingNewSchedule);
    setNewScheduleName("");
    setSchedule({
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
      Sunday: [],
    });
  };

  const closeSchedule = () => {
    setShowScheduleForm(false);
    setSlotsBooked(false);
  };

  const isFormValid = () => {
    const {
      ssid,
      band,
      networkName,
      vlanId,
      securityAuthentication,
      password,
      scheduleEnabled,
    } = formData;

    const isSSIDValid = isSsidName(ssid);
    const isBandValid = Array.isArray(band) ? band.length > 0 : Boolean(band);
    const isNetworkValid = String(networkName || "").trim() !== "";
    const isVlanValid = isVlanId(vlanId);
    const isSecurityValid = String(securityAuthentication || "").trim() !== "";
    const isPasswordValid =
      !needsWpaPassword(securityAuthentication) || isWpaPassword(password);

    let isScheduleValid = scheduleEnabled !== "1";
    if (scheduleEnabled === "1") {
      if (selectedExistingSchedule || formData.selectedSchedule) {
        isScheduleValid = true;
      }
      if (
        isCreatingNewSchedule &&
        scheduleName.trim() !== "" &&
        Object.values(schedule).some((day) => day.length > 0)
      ) {
        isScheduleValid = true;
      }
    }

    return (
      isSSIDValid &&
      isBandValid &&
      isNetworkValid &&
      isVlanValid &&
      isSecurityValid &&
      isPasswordValid &&
      isScheduleValid
    );
  };

  return (
    <>
      {showPopUp && (
        <div>
          <div className="customModal">
            <div className="c-modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {localizationStrings?.addNewSSID}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handlePopUpClose}
                  ></button>
                </div>
                <div className="modal-body">
                  <form>
                    <div className="add-ssid-body">
                      <h4>{localizationStrings?.wirelessSettings}</h4>
                      <div className="row d-flex">
                        <div className="col-lg-6">
                          <input
                            type="text"
                            className="form-control"
                            name="ssid"
                            placeholder="SSID"
                            value={formData.ssid}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-lg-6">
                          <div className="switch-box">
                            <strong>
                              {localizationStrings?.broadcastSSID}
                            </strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="broadcastStatus"
                                checked={formData.broadcastStatus === "1"}
                                onChange={handleChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="border-line"></div>

                      <h4 className="mt-3">
                        {localizationStrings?.band}
                        <span className="text-danger">*</span>
                      </h4>
                      <p>{localizationStrings?.wpaInfo}</p>
                      <div className="row">
                        <div className="col-lg-3 checkbox-group">
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="band"
                              value="All"
                              checked={formData.band.length === 2}
                              onChange={handleChange}
                            />
                            <span className="indicator"></span>
                            <strong>{localizationStrings?.all}</strong>
                          </label>
                        </div>
                        <div className="col-lg-3 checkbox-group">
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="band"
                              value="2.4 GHz"
                              checked={formData.band.includes("2.4 GHz")}
                              onChange={handleChange}
                            />
                            <span className="indicator"></span>
                            <strong>2.4 GHz</strong>
                          </label>
                        </div>
                        <div className="col-lg-3 checkbox-group">
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="band"
                              value="5 GHz"
                              checked={formData.band.includes("5 GHz")}
                              onChange={handleChange}
                            />
                            <span className="indicator"></span>
                            <strong>5 GHz</strong>
                          </label>
                        </div>
                      </div>

                      <div className="border-line"></div>

                      <h4 className="mt-3">
                        {localizationStrings?.securitySettings}
                      </h4>
                      <div className="row">
                        <div className="col-lg-6">
                          <label htmlFor="security" className="col-form-label">
                            {localizationStrings?.security}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            id="securityAuthentication"
                            className="form-select"
                            name="securityAuthentication"
                            placeholder="Select Security"
                            value={formData.securityAuthentication}
                            onChange={handleChange}
                          >
                            <option value="">
                              {localizationStrings?.selectSecurity}
                            </option>
                            <option value="Open">
                              {localizationStrings?.open}
                            </option>
                            <option value="WPA2 Personal">
                              {localizationStrings?.WPA2Personal}
                            </option>
                            <option value="WPA2 Personal Mixed">
                              {localizationStrings?.WPA2PersonalMixed}
                            </option>
                            <option value="WPA3 Personal">
                              {localizationStrings?.WPA3Personal}
                            </option>
                            <option value="WPA3 Personal Mixed">
                              {localizationStrings?.WPA3PersonalMixed}
                            </option>
                          </select>
                        </div>
                      </div>
                      {(formData.securityAuthentication === "WPA2 Personal" ||
                        formData.securityAuthentication ===
                          "WPA2 Personal Mixed") && (
                        <>
                          <div className="row mt-3">
                            <div className="col-lg-12">
                              <div className="switch-box">
                                <strong>
                                  {localizationStrings?.multiPresharedKey}
                                </strong>
                                <label className="ice-switch">
                                  <input
                                    type="checkbox"
                                    name="multiPsK"
                                    checked={formData.multiPsK}
                                    onChange={() =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        multiPsK: !prev.multiPsK,
                                      }))
                                    }
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="row mt-3">
                            <div className="col-lg-6">
                              <label
                                htmlFor="password"
                                className="col-form-label"
                              >
                                {localizationStrings?.password}
                                <span className="text-danger">*</span>
                              </label>
                              <div className="input-wrapper">
                                <input
                                  type={showPassword ? "text" : "password"}
                                  className="form-control"
                                  name="password"
                                  placeholder="Password"
                                  value={formData.password}
                                  onChange={handleChange}
                                />
                                <img
                                  src={showPassword ? eyeIcon : eyeCloseIcon}
                                  alt={
                                    showPassword
                                      ? "Hide password"
                                      : "Show password"
                                  }
                                  className="show-password"
                                  onClick={togglePasswordVisibility}
                                  style={{ cursor: "pointer" }}
                                />
                              </div>
                              {formData.password && !isWpaPassword(formData.password) && (
                                <p className="text-danger mt-1">Password must be 8-63 characters.</p>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      {(formData.securityAuthentication === "WPA3 Personal" ||
                        formData.securityAuthentication ===
                          "WPA3 Personal Mixed") && (
                        <div className="row mt-3">
                          <div className="col-lg-6">
                            <label
                              htmlFor="password"
                              className="col-form-label"
                            >
                              {localizationStrings?.password}
                              <span className="text-danger">*</span>
                            </label>
                            <div className="input-wrapper">
                              <input
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                name="password"
                                placeholder="Password"
                                value={formData.password}
                                onChange={handleChange}
                              />
                              <img
                                src={showPassword ? eyeIcon : eyeCloseIcon}
                                alt={
                                  showPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                className="show-password"
                                onClick={togglePasswordVisibility}
                                style={{ cursor: "pointer" }}
                              />
                            </div>
                            {formData.password && !isWpaPassword(formData.password) && (
                              <p className="text-danger mt-1">Password must be 8-63 characters.</p>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="border-line"></div>
                      <div className="row mt-3">
                        <div className="col-lg-12">
                          <label
                            className="col-form-label"
                            style={{ marginBottom: "8px" }}
                          >
                            802.11w (PMF)
                          </label>
                          <div className="d-flex">
                            <div className="col-lg-4">
                              <div className="switch-box">
                                <strong>Mandatory</strong>
                                <label className="ice-switch">
                                  <input
                                    type="radio"
                                    name="ieee80211w"
                                    value="2"
                                    checked={formData.ieee80211w === "2"}
                                    onChange={handleChange}
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                            </div>
                            <div className="col-lg-4">
                              <div className="switch-box">
                                <strong>Optional</strong>
                                <label className="ice-switch">
                                  <input
                                    type="radio"
                                    name="ieee80211w"
                                    value="1"
                                    checked={formData.ieee80211w === "1"}
                                    onChange={handleChange}
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                            </div>
                            <div className="col-lg-4">
                              <div className="switch-box">
                                <strong>Disable</strong>
                                <label className="ice-switch">
                                  <input
                                    type="radio"
                                    name="ieee80211w"
                                    value="0"
                                    checked={formData.ieee80211w === "0"}
                                    onChange={handleChange}
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border-line"></div>
                      <h4 className="mt-3">
                        {localizationStrings?.networkSettings}
                      </h4>
                      <div className="row">
                        <div className="col-lg-6">
                          <label
                            htmlFor="networkName"
                            className="col-form-label"
                          >
                            {localizationStrings?.networkName}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            id="networkName"
                            className="form-select"
                            name="networkName"
                            value={formData.networkName}
                            onChange={handleChange}
                          >
                            <option value="">
                              {localizationStrings?.selectNetwork}
                            </option>
                            {networkIds.map((net) => (
                              <option
                                key={net.network_id}
                                value={net.network_id}
                              >
                                {net.network_name || net.network_id}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-lg-6">
                          <label htmlFor="vlanId" className="col-form-label">
                            {localizationStrings?.vlanId}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            id="vlanId"
                            className="form-select"
                            name="vlanId"
                            value={formData.vlanId}
                            onChange={handleChange}
                          >
                            <option value="">
                              {localizationStrings?.selectVlan}
                            </option>
                            {vlanList.map((vlan) => (
                              <option key={vlan.vlan_id} value={vlan.vlan_id}>
                                {vlan.vlan_name} - {vlan.vlan_id}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="border-line"></div>
                      <div className="row mt-3">
                        <div className="col-lg-12">
                          <div className="switch-box">
                            <strong>
                              {localizationStrings?.clientIsolation}{" "}
                              <span className="text-danger">*</span>
                            </strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="clientIsolation"
                                checked={formData.clientIsolation === "1"}
                                onChange={handleChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="border-line"></div>
                      <div className="row mt-3">
                        <div className="col-lg-12">
                          <div className="switch-box">
                            <strong>
                              {localizationStrings?.accessToLocalUI}{" "}
                              <span className="text-danger">*</span>
                            </strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="allowLocalUIAccess"
                                checked={formData.allowLocalUIAccess === "1"}
                                onChange={handleChange}
                                disabled={formData.clientIsolation !== "1"}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="border-line"></div>
                      <h4 className="mt-3 mb-4">
                        {localizationStrings?.radioSettings}
                      </h4>
                      <div className="row">
                        <div className="col-lg-4">
                          <div className="switch-box">
                            <strong>
                              {localizationStrings?.bandSteering}
                              <span className="text-danger">*</span>
                            </strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="bandSteeringSt"
                                checked={formData.bandSteeringSt === "1"}
                                onChange={handleChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                        <div className="col-lg-4 mb-3">
                          <div className="switch-box fast-roaming-box">
                            <div className="label-wrap">
                              <strong>
                                {localizationStrings?.fastRoaming}
                              </strong>
                              <div className="sub-label">
                                (802.11r) <span className="text-danger">*</span>
                              </div>
                            </div>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="fastRoamingSt"
                                checked={formData.fastRoamingSt === "1"}
                                onChange={handleChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>

                        <div className="col-lg-4">
                          <div className="switch-box">
                            <strong>802.11k/v</strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="kvrStatus"
                                checked={formData.kvrStatus === "1"}
                                onChange={handleChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="border-line"></div>
                        <div className="row mt-3">
                          <div className="col-lg-12">
                            <div className="switch-box">
                              <strong>
                                {localizationStrings?.enableSSIDSchedule}{" "}
                                <span className="text-danger">*</span>
                              </strong>
                              <label className="ice-switch">
                                <input
                                  type="checkbox"
                                  name="scheduleEnabled"
                                  checked={formData.scheduleEnabled === "1"}
                                  onChange={handlePopUp}
                                />
                                <span className="slider"></span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {formData.scheduleEnabled === "1" && (
                          <>
                            <div className="row mt-3">
                              <div className="col-lg-12">
                                <label
                                  htmlFor="selectedSchedule"
                                  className="col-form-label"
                                >
                                  {localizationStrings?.selectSchedule}
                                </label>
                                <select
                                  className="form-select"
                                  value={selectedExistingSchedule}
                                  disabled={isCreatingNewSchedule}
                                  onChange={(e) => {
                                    const value = e.target.value;

                                    setSelectedExistingSchedule(value);
                                    setIsCreatingNewSchedule(false);
                                    setScheduleName("");

                                    const selected = schedules.find(
                                      (s) => s.scheduleName === value
                                    );

                                    setFormData((prev) => ({
                                      ...prev,
                                      selectedSchedule: value,
                                      scheduleSetting: selected
                                        ? selected.scheduleSetting
                                        : {},
                                    }));
                                  }}
                                >
                                  <option value="">
                                    {localizationStrings?.selectSchedule}
                                  </option>

                                  {schedules.map((schedule) => (
                                    <option
                                      key={schedule.scheduleId}
                                      value={schedule.scheduleName}
                                    >
                                      {schedule.scheduleName}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <p className="or-seperator">
                              <span>{localizationStrings?.or}</span>
                            </p>

                            <div className="row mt-2">
                              <div className="col-lg-12">
                                <strong
                                  className={`pointer ${
                                    selectedExistingSchedule ? "text-muted" : ""
                                  }`}
                                  onClick={() => {
                                    if (selectedExistingSchedule) return;
                                    setIsCreatingNewSchedule(true);
                                    setSelectedExistingSchedule("");
                                  }}
                                >
                                  <i
                                    className="fa fa-plus"
                                    aria-hidden="true"
                                  ></i>{" "}
                                  {localizationStrings?.configureNewSchedule}
                                </strong>
                              </div>
                            </div>

                            {isCreatingNewSchedule &&
                              showScheduleForm &&
                              !slotsBooked && (
                                <div className="schedule-week mt-4">
                                  <h4>{localizationStrings?.scheduleName}</h4>
                                  <div className="row">
                                    <div className="col-lg-6">
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Schedule Name"
                                        value={scheduleName}
                                        onChange={(e) => {
                                          setScheduleName(e.target.value);
                                        }}
                                      />
                                    </div>
                                  </div>

                                  <div className="border-line mt-3"></div>
                                  <p>
                                    {localizationStrings?.locationTimezone}-
                                    America/New_York (ET)
                                  </p>
                                  <h4>{localizationStrings?.days}</h4>
                                  <p>
                                    {localizationStrings?.slotSelectionInfo}
                                  </p>

                                  <div className="schedule-slot-scroller">
                                    {Object.keys(schedule).map((day) => (
                                      <div className="schedule-days" key={day}>
                                        <div className="sc-day-title">
                                          <h4>{day}</h4>
                                          <div>
                                            <button
                                              type="button"
                                              className="icon-btn"
                                              onClick={() => addTimeSlot(day)}
                                            >
                                              <i
                                                className="fa fa-plus"
                                                aria-hidden="true"
                                              ></i>
                                            </button>
                                          </div>
                                        </div>

                                        <div className="schedule-time">
                                          {schedule[day].map((slot, index) => (
                                            <div
                                              className="schedule-time-box"
                                              key={index}
                                            >
                                              <div className="start-time">
                                                <h4>
                                                  {localizationStrings?.startOn}
                                                </h4>
                                                <TimePicker
                                                  value={
                                                    slot.startTime
                                                      ? dayjs(
                                                          slot.startTime,
                                                          "HHmm"
                                                        )
                                                      : null
                                                  }
                                                  onChange={(newValue) => {
                                                    const formattedTime =
                                                      newValue
                                                        ? newValue.format(
                                                            "HHmm"
                                                          )
                                                        : "";
                                                    handleScheduleChange(
                                                      day,
                                                      index,
                                                      "startTime",
                                                      formattedTime
                                                    );
                                                  }}
                                                />
                                              </div>

                                              <div className="end-time">
                                                <h4>
                                                  {localizationStrings?.endOn}
                                                </h4>
                                                <TimePicker
                                                  value={
                                                    slot.endTime
                                                      ? dayjs(
                                                          slot.endTime,
                                                          "HHmm"
                                                        )
                                                      : null
                                                  }
                                                  onChange={(newValue) => {
                                                    const formattedTime =
                                                      newValue
                                                        ? newValue.format(
                                                            "HHmm"
                                                          )
                                                        : "";
                                                    handleScheduleChange(
                                                      day,
                                                      index,
                                                      "endTime",
                                                      formattedTime
                                                    );
                                                  }}
                                                />
                                              </div>

                                              <button
                                                type="button"
                                                className="icon-btn"
                                                onClick={() => addTimeSlot(day)}
                                              >
                                                <i
                                                  className="fa fa-plus"
                                                  aria-hidden="true"
                                                ></i>
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </>
                        )}
                      </LocalizationProvider>
                    </div>
                  </form>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handlePopUpClose}
                  >
                    {localizationStrings?.cancel}
                  </button>

                  <button
                    type="button"
                    className={`text-btn primary-btn ${
                      !isFormValid() || loading ? "disabled" : ""
                    }`}
                    onClick={(e) => handleSubmit(e, "save")}
                    disabled={!isFormValid() || loading}
                  >
                    {loading ? "Saving..." : localizationStrings?.save}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showData && (
        <Modal show={showData} onHide={handleSuccessClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>{localizationStrings?.success}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{localizationStrings?.ssidCreatedSuccessfully}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleSuccessClose}>
              {localizationStrings?.ok}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {showSsidLimtPopup && (
        <div className="customModal confirm-modal">
          <div className="modal-dialog modal-dialog-centered small">
            <div className="modal-content inc-modal">
              <div className="modal-body">
                <img src={errorIcon} alt="error" />
                <h3>{localizationStrings?.ssidLimit}</h3>
                <div className="modal-footer">
                  <button
                    className="text-btn primary-btn"
                    onClick={() => setShowSsidLimitPopup(false)}
                  >
                    {localizationStrings?.close}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AddSSIDModal;
